import React from 'react';
import { FileText } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

/**
 * DocumentsList Component
 * Displays user's documents with summaries and focus tags
 */
export default function DocumentsList({ documents, onSelectDocument, focuses = [] }) {
  if (!documents || documents.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center text-neutral-400">
          <FileText size={32} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No documents yet</p>
          <p className="text-xs mt-1">Click "New Doc" to get started</p>
        </div>
      </div>
    );
  }

  // Helper to get focus name by ID
  const getFocusName = (focusId) => {
    const focus = focuses.find(f => f.id === focusId);
    return focus?.title || 'Focus';
  };

  return (
    <div className="flex-1 overflow-y-auto px-6 pt-3 pb-4">
      <div className="space-y-3">
        {documents.map((doc) => (
          <button
            key={doc.id}
            onClick={() => onSelectDocument(doc)}
            className="w-full text-left p-4 rounded-xl bg-white border border-neutral-200 hover:border-purple-300 hover:shadow-md transition-all duration-200 group"
          >
            {/* Header with Title and Focus Pills */}
            <div className="flex items-start justify-between gap-3 mb-1.5">
              {/* Title */}
              <h4 className="font-semibold text-neutral-900 text-sm group-hover:text-purple-600 transition-colors flex-1 min-w-0">
                {doc.title || 'Untitled'}
              </h4>

              {/* Focus Pills - Top Right */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {doc.focus_ids && doc.focus_ids.length > 0 && doc.focus_ids.slice(0, 1).map(focusId => (
                  <span
                    key={focusId}
                    className="px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full text-[10px] font-medium border border-purple-200"
                  >
                    {getFocusName(focusId)}
                  </span>
                ))}
                {doc.focus_ids && doc.focus_ids.length > 1 && (
                  <span className="px-2 py-0.5 bg-neutral-50 text-neutral-500 rounded-full text-[10px] font-medium">
                    +{doc.focus_ids.length - 1}
                  </span>
                )}
              </div>
            </div>
            
            {/* Summary */}
            <p className="text-xs text-neutral-500 mb-2.5 line-clamp-2 leading-relaxed">
              {doc.summary || 'No content'}
            </p>

            {/* Footer with Timestamp */}
            <div className="flex items-center justify-end">
              <span className="text-[10px] text-neutral-400 whitespace-nowrap">
                {formatDistanceToNow(new Date(doc.updated_at), { addSuffix: true })}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

